import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  Alert,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Fade,
  Grow,
  Slide
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  ThumbUp as ThumbUpIcon,
  CalendarToday as CalendarIcon,
  Language as LanguageIcon,
  Audiotrack as AudioIcon,
  VideoLibrary as VideoIcon,
  Speed as SpeedIcon,
  Subtitles as SubtitlesIcon,
  History as HistoryIcon,
  CloudDownload as CloudDownloadIcon
} from '@mui/icons-material';
import { useDownload } from '../contexts/DownloadContext';
import toast from 'react-hot-toast';

// Tab Panel Component
const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ marginTop: 16 }}>
    {value === index && children}
  </div>
);

const DownloadPage = () => {
  const {
    videoInfo,
    loading,
    fetchMediaInfo,
    downloadMedia,
    supportedPlatforms,
    fetchSupportedPlatforms
  } = useDownload();

  const [url, setUrl] = useState('');
  const [format, setFormat] = useState('video');
  const [quality, setQuality] = useState('1080p');
  const [platform, setPlatform] = useState('youtube');
  const [includeSubtitles, setIncludeSubtitles] = useState(false);
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [availableQualities, setAvailableQualities] = useState([]);
  const [detectedPlatform, setDetectedPlatform] = useState(null);

  useEffect(() => {
    fetchSupportedPlatforms();
  }, []);

  // Auto-detect platform from URL
  useEffect(() => {
    if (url) {
      const detected = detectPlatformFromUrl(url);
      setDetectedPlatform(detected);
      if (detected && supportedPlatforms.includes(detected)) {
        setPlatform(detected);
      }
    }
  }, [url, supportedPlatforms]);

  const detectPlatformFromUrl = (url) => {
    const patterns = {
      youtube: /(youtube\.com|youtu\.be)/i,
      facebook: /(facebook\.com|fb\.watch)/i,
      instagram: /instagram\.com/i,
      twitter: /(twitter\.com|x\.com)/i,
      tiktok: /tiktok\.com/i,
      vimeo: /vimeo\.com/i,
      dailymotion: /dailymotion\.com/i,
      twitch: /twitch\.tv/i,
      soundcloud: /soundcloud\.com/i,
      spotify: /spotify\.com/i
    };

    for (const [platform, pattern] of Object.entries(patterns)) {
      if (pattern.test(url)) return platform;
    }
    return null;
  };

  const handleFetchInfo = async () => {
    if (!url.trim()) {
      toast.error('Please enter a URL');
      return;
    }
    
    try {
      const info = await fetchMediaInfo(url);
      if (info && info.availableQualities) {
        setAvailableQualities(info.availableQualities);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  const handleDownload = async () => {
    if (!videoInfo) {
      toast.error('Please fetch media info first');
      return;
    }

    try {
      await downloadMedia(videoInfo.id, {
        format,
        quality,
        platform,
        includeSubtitles,
        includeMetadata
      });
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('URL copied to clipboard');
  };

  const qualityOptions = {
    video: {
      youtube: ['best', '2160p', '1440p', '1080p', '720p', '480p', '360p', 'worst'],
      vimeo: ['best', '4K', 'HD', 'SD'],
      facebook: ['best', 'HD', 'SD'],
      instagram: ['best', 'HD', 'SD'],
      default: ['best', 'high', 'medium', 'low']
    },
    audio: {
      youtube: ['best', '320kbps', '256kbps', '192kbps', '128kbps', 'worst'],
      soundcloud: ['best', 'high', 'medium', 'low'],
      spotify: ['best', 'high', 'medium', 'low'],
      default: ['best', '320kbps', '256kbps', '192kbps', '128kbps', 'worst']
    }
  };

  const getQualitiesForPlatform = () => {
    const platformQualities = qualityOptions[format]?.[platform] || qualityOptions[format]?.default || [];
    return availableQualities.length ? availableQualities : platformQualities;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getPlatformIcon = (platformName) => {
    const icons = {
      youtube: '🎬',
      facebook: '📘',
      instagram: '📷',
      twitter: '🐦',
      tiktok: '🎵',
      vimeo: '🎥',
      dailymotion: '📺',
      twitch: '🎮',
      soundcloud: '🎧',
      spotify: '🎵'
    };
    return icons[platformName] || '🌐';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grow in={true} timeout={800}>
        <Grid container spacing={3}>
          {/* Left Panel - Download Form */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                <CloudDownloadIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                Universal Media Downloader
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Download videos and audio from 20+ platforms including YouTube, Facebook, Instagram, TikTok, and more.
                No registration required, completely free with unlimited downloads.
              </Typography>

              {/* URL Input with Platform Detection */}
              <Box sx={{ mb: 3 }}>
                <FormLabel sx={{ mb: 1, display: 'block' }}>Media URL</FormLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="Paste any video/audio URL here..."
                    disabled={loading}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.default',
                      }
                    }}
                    InputProps={{
                      startAdornment: detectedPlatform && (
                        <Box component="span" sx={{ mr: 1, fontSize: '1.2rem' }}>
                          {getPlatformIcon(detectedPlatform)}
                        </Box>
                      ),
                    }}
                  />
                  <Tooltip title="Copy URL">
                    <IconButton onClick={handleCopyUrl} disabled={!url}>
                      {copied ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
                {detectedPlatform && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                      icon={<LanguageIcon />}
                      label={`Detected: ${detectedPlatform.charAt(0).toUpperCase() + detectedPlatform.slice(1)}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    {!supportedPlatforms.includes(detectedPlatform) && (
                      <Chip
                        label="Limited Support"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                )}
              </Box>

              {/* Fetch Info Button */}
              <Button
                fullWidth
                variant="contained"
                onClick={handleFetchInfo}
                disabled={loading || !url.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                size="large"
                sx={{ mb: 3, py: 1.5 }}
              >
                {loading ? 'Fetching...' : 'Get Media Info'}
              </Button>

              <Divider sx={{ my: 3 }} />

              {/* Platform Selection */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <FormLabel sx={{ mb: 1 }}>Platform</FormLabel>
                <Select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  disabled={loading}
                  sx={{ backgroundColor: 'background.default' }}
                >
                  {supportedPlatforms.map((p) => (
                    <MenuItem key={p} value={p}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{getPlatformIcon(p)}</span>
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Download Options Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                  <Tab icon={<VideoIcon />} label="Video" />
                  <Tab icon={<AudioIcon />} label="Audio" />
                  <IconButton sx={{ ml: 'auto' }}>
                    <HistoryIcon />
                  </IconButton>
                </Tabs>
              </Box>

              <TabPanel value={activeTab} index={0}>
                {/* Video Options */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <FormLabel sx={{ mb: 1 }}>Video Quality</FormLabel>
                  <Select
                    value={quality}
                    onChange={(e) => setQuality(e.target.value)}
                    disabled={loading}
                    sx={{ backgroundColor: 'background.default' }}
                    startAdornment={<SpeedIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                  >
                    {getQualitiesForPlatform().map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                {/* Audio Options */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <FormLabel sx={{ mb: 1 }}>Audio Quality</FormLabel>
                  <Select
                    value={quality}
                    onChange={(e) => setQuality(e.target.value)}
                    disabled={loading}
                    sx={{ backgroundColor: 'background.default' }}
                  >
                    {getQualitiesForPlatform().map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TabPanel>

              {/* Advanced Options */}
              <Box sx={{ mb: 3 }}>
                <FormLabel sx={{ mb: 1, display: 'block' }}>Advanced Options</FormLabel>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeSubtitles}
                      onChange={(e) => setIncludeSubtitles(e.target.checked)}
                      disabled={format === 'audio' || platform !== 'youtube'}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SubtitlesIcon fontSize="small" />
                      <span>Include subtitles (if available)</span>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeMetadata}
                      onChange={(e) => setIncludeMetadata(e.target.checked)}
                    />
                  }
                  label="Include metadata (title, artist, thumbnail)"
                />
              </Box>

              {/* Download Button */}
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={handleDownload}
                disabled={!videoInfo || loading}
                startIcon={<DownloadIcon />}
                sx={{ 
                  py: 1.5,
                  fontSize: '1.1rem',
                  background: 'linear-gradient(45deg, #FF3B30 30%, #FF6B4A 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FF3B30 20%, #FF6B4A 80%)',
                  }
                }}
              >
                Download Now
              </Button>

              <Alert severity="info" sx={{ mt: 3 }}>
                <InfoIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                <Typography variant="body2">
                  Files download directly to your device. Your privacy is protected - we don't store any files on our servers.
                  Supported platforms: {supportedPlatforms.join(', ')}
                </Typography>
              </Alert>
            </Paper>
          </Grid>

          {/* Right Panel - Media Preview */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                <PlayArrowIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'secondary.main' }} />
                Media Preview
              </Typography>

              {videoInfo ? (
                <Fade in={true} timeout={1000}>
                  <Card>
                    {videoInfo.thumbnail && (
                      <Box sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="350"
                          image={videoInfo.thumbnail}
                          alt={videoInfo.title}
                          sx={{ 
                            objectFit: 'cover',
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12
                          }}
                        />
                        <Chip
                          label={formatDuration(videoInfo.duration)}
                          size="small"
                          sx={{
                            position: 'absolute',
                            bottom: 16,
                            right: 16,
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                        {videoInfo.platform && (
                          <Chip
                            label={videoInfo.platform}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 16,
                              left: 16,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                        )}
                      </Box>
                    )}
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, lineHeight: 1.4 }}>
                        {videoInfo.title}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        {videoInfo.uploader && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {videoInfo.uploader}
                            </Typography>
                          </Box>
                        )}
                        {videoInfo.viewCount && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <VisibilityIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {formatNumber(videoInfo.viewCount)} views
                            </Typography>
                          </Box>
                        )}
                        {videoInfo.likeCount && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ThumbUpIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {formatNumber(videoInfo.likeCount)}
                            </Typography>
                          </Box>
                        )}
                        {videoInfo.uploadDate && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {videoInfo.uploadDate}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {videoInfo.description && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ maxHeight: 100, overflow: 'auto' }}>
                            {videoInfo.description}
                          </Typography>
                        </>
                      )}

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Available Formats:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {videoInfo.formats?.map((format, index) => (
                          <Chip
                            key={index}
                            label={format}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              borderColor: 'divider',
                              '&:hover': { borderColor: 'primary.main' }
                            }}
                          />
                        ))}
                      </Box>

                      {videoInfo.filesize && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                          <Chip
                            icon={<DownloadIcon />}
                            label={`Size: ${formatNumber(videoInfo.filesize)}`}
                            size="small"
                            color="success"
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Fade>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '500px',
                    color: 'text.secondary',
                    backgroundColor: 'background.default',
                    borderRadius: 2,
                    p: 4
                  }}
                >
                  <SearchIcon sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                  <Typography variant="h6" align="center" gutterBottom sx={{ fontWeight: 500 }}>
                    No Media Selected
                  </Typography>
                  <Typography variant="body2" align="center" color="text.secondary" sx={{ maxWidth: 400 }}>
                    Enter any video/audio URL and click "Get Media Info" to see details and start downloading
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grow>
    </Container>
  );
};

export default DownloadPage;